package ui

import io.kvision.core.AlignItems
import io.kvision.core.Background
import io.kvision.utils.perc
import io.kvision.core.BoxShadow
import io.kvision.core.Border
import io.kvision.core.BorderStyle
import io.kvision.core.Col
import io.kvision.core.Color
import io.kvision.core.Display
import io.kvision.core.FlexDirection
import io.kvision.core.FontWeight
import io.kvision.core.Overflow
import io.kvision.core.Position
import io.kvision.core.JustifyContent
import io.kvision.panel.hPanel
import io.kvision.core.TextAlign
import io.kvision.core.onClick
import io.kvision.core.onInput
import io.kvision.core.onEvent
import io.kvision.form.text.TextInput
import io.kvision.form.text.TextArea
import io.kvision.form.text.textInput
import io.kvision.form.text.textArea
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h2
import io.kvision.html.h3
import io.kvision.html.icon
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.panel.SimplePanel
import io.kvision.panel.vPanel
import io.kvision.utils.px
import kotlinx.browser.document
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import org.w3c.files.FileReader
import org.khronos.webgl.Uint8Array
import org.ttt.autogenesis.kvisionapp.ServerExtendBridge
import globals.KEnv
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.MapUploadGateResponse
import org.ttt.autogenesis.network.MapUploadRequest
import org.ttt.autogenesis.network.RpcJson

/**
 * 4-state upload modal: idle / validated / invalid / publishing.
 *
 * Auto-height sizing follows the kvision-modal-sizing-recipes.md recipe #1
 * (no `bottom`, no `height: 100%`). The 4 internal states are toggled via
 * the `data-state` attribute on the drop zone + meta preview + error banner.
 *
 * Server-side upload RPC is wired through TODO_AFTER_UPLOAD_RPC markers —
 * the modal shows the metadata card via a placeholder when a file is selected.
 * The `data-testid` attributes are stable hooks for the server-side hookup.
 */
class MapUploadModal : SimplePanel(className = "map-upload-modal")
{
    private enum class State { IDLE, VALIDATED, INVALID, PUBLISHING }

    private var currentState: State = State.IDLE

    private lateinit var dropZone: SimplePanel
    private lateinit var fileInput: HTMLInputElement
    private lateinit var metaPreview: SimplePanel
    private lateinit var errorBanner: SimplePanel
    private lateinit var nameInput: TextInput
    private lateinit var descriptionInput: TextArea
    private var selectedFile: File? = null

    init
    {
        setAttribute("data-testid", "map-upload-modal")
        width = 580.px
        position = Position.FIXED
        top = 80.px
        setStyle("left", "calc(50% - 290px)")
        zIndex = 9100
        background = Background(Color("rgba(7, 10, 24, 0.95)"))
        border = Border(2.px, BorderStyle.SOLID, Color("rgba(94, 106, 220, 0.4)"))
        boxShadow = BoxShadow(0.px, 25.px, 50.px, color = Color("rgba(0, 0, 0, 0.85)"))
        flexDirection = FlexDirection.COLUMN
        display = Display.NONE
        padding = 0.px
        overflow = Overflow.HIDDEN

        // Mobile layout listener — same shape as CollectionOverlay.kt:385-414 and
        // MainMenu.kt:250-280. The break-even point is pinned at (max-width: 600px) /
        // (max-height: 600px) + portrait so every widget reacts to the same viewport
        // threshold and the e2e probe can flip state by resizing the page. The
        // load-bearing CSS rules live in night-mode.css under the CollectionOverlay
        // @media block — this listener only owns the attribute, mirroring the parent
        // convention. Adding this listener ensures the modal's data-mobile-layout
        // reflects the viewport independently of its parent CollectionOverlay, which
        // matters when the modal is mounted standalone (e.g. from the MapDetailWindow
        // delete-replacement path).
        kotlinx.coroutines.GlobalScope.launch {
            try {
                val mediaQuery = kotlinx.browser.window.matchMedia(
                    "(max-width: 600px), (max-height: 600px) and (orientation: portrait)"
                )
                val updateAttribute: (Boolean) -> Unit = { matches ->
                    setAttribute("data-mobile-layout", if (matches) "portrait" else "desktop")
                }
                updateAttribute(mediaQuery.matches)
                mediaQuery.addEventListener("change", { event ->
                    val mql = event.asDynamic()
                    updateAttribute(mql.matches as Boolean)
                })
                Logger.info(
                    LogCategory.UI,
                    "MapUploadModal: matchMedia listener attached, current state=${if (mediaQuery.matches) "portrait" else "desktop"}"
                )
            } catch (err: Throwable) {
                Logger.warn(
                    LogCategory.UI,
                    "MapUploadModal: matchMedia listener failed to attach (non-fatal): ${err.message}"
                )
            }
        }

        // Header — align center so the X button is vertically aligned with
        // the "Upload Map Pack" h2 title (was misaligned left/right with the
        // title before).
        vPanel(
            className = "map-upload-modal-header",
            alignItems = AlignItems.CENTER,
            justify = JustifyContent.SPACEBETWEEN
        ) {
            width = 100.perc
            h2("Upload Map Pack") {
                color = Color.name(Col.WHITE)
                marginTop = 0.px
                marginBottom = 0.px
                fontSize = 16.px
            }
            button("", icon = "fas fa-times", className = "btn btn-play btn-close-collection map-upload-modal-close") {
                width = 32.px
                height = 32.px
                onClick { dismiss() }
                setAttribute("data-testid", "map-upload-close")
            }
        }

        // Body
        vPanel(className = "map-upload-modal-body") {
            width = 100.perc
            flexGrow = 1
            overflow = Overflow.AUTO
            setStyle("min-height", "0")
            setStyle("max-height", "100%")

            // Drop zone (visible in all 4 states)
            dropZone = SimplePanel(className = "drop-zone") {
                width = 100.perc
                setAttribute("data-testid", "map-upload-drop-zone")
                setAttribute("data-state", "idle")
                onEvent { click = { fileInput.click() } }
                vPanel(alignItems = AlignItems.CENTER, spacing = 6) {
                    width = 100.perc
                    icon("fas fa-cloud-arrow-up") {
                        fontSize = 32.px
                        color = Color("#a8b1d1")
                    }
                    p("Drop a .map file here") {
                        fontSize = 14.px
                        fontWeight = FontWeight.BOLD
                        color = Color.name(Col.WHITE)
                    }
                    p {
                        fontSize = 12.px
                        color = Color("#6b7290")
                        +"or "
                        span("browse from your computer") {
                            color = Color("#2942e0")
                            setAttribute("data-testid", "map-upload-browse-link")
                            onEvent { click = {
                                Logger.info(LogCategory.UI, "MapUploadModal: browse-link click")
                            } }
                        }
                        +" — up to 50 MB"
                    }
                }
            }
            add(dropZone)

            // Hidden file picker (clicked programmatically when drop zone is clicked).
            // Mirrors the Mp3AssetLoader pattern: keep the raw HTMLInputElement off
            // the KVision container and attach it to document.body so the browser
            // still owns it (programmatic .click() works) and the data-testid
            // remains queryable for probes. Avoids the
            //   `this.add(input as io.kvision.core.Component)` ClassCastException
            // that SimplePanel#add enforces at runtime.
            val input = (document.createElement("input") as HTMLInputElement).apply {
                type = "file"
                accept = ".map"
                style.display = "none"
                setAttribute("data-testid", "map-upload-file-input")
                onchange = { event ->
                    val target = event.target as HTMLInputElement
                    val files = target.files
                    if (files != null && files.length > 0) {
                        handleFileSelected(files.item(0)!!)
                    }
                    null
                }
            }
            fileInput = input
            document.body?.appendChild(input)

            // Metadata preview (visible in VALIDATED + PUBLISHING)
            metaPreview = SimplePanel(className = "meta-preview") {
                width = 100.perc
                display = Display.NONE
                setAttribute("data-testid", "map-upload-meta-preview")
            }
            add(metaPreview)

            // Error banner (visible in INVALID)
            errorBanner = SimplePanel(className = "error-banner") {
                width = 100.perc
                display = Display.NONE
                setAttribute("data-testid", "map-upload-error-banner")
            }
            add(errorBanner)

            // Name override field
            p("Map name (optional override)") {
                fontSize = 12.px
                fontWeight = FontWeight.BOLD
                color = Color("#a8b1d1")
                marginTop = 16.px
                marginBottom = 6.px
            }
            nameInput = textInput {
                width = 100.perc
                placeholder = "Defaults to the filename minus extension"
                setAttribute("data-testid", "map-upload-name-input")
            }
            add(nameInput)

            // Description field
            p("Description (shown in Collection)") {
                fontSize = 12.px
                fontWeight = FontWeight.BOLD
                color = Color("#a8b1d1")
                marginTop = 16.px
                marginBottom = 6.px
            }
            descriptionInput = textArea {
                width = 100.perc
                placeholder = "A short pitch — what makes this map interesting?"
                rows = 3
                // Match the name input's width by setting a high cols value
                // (textArea defaults to cols=60 which KVision's wrapper ignores
                // but the inner <textarea> honors).
                setAttribute("cols", "60")
                setAttribute("data-testid", "map-upload-description-input")
            }
            add(descriptionInput)

            // Info text
            p {
                fontSize = 11.px
                color = Color("#6b7290")
                setAttribute("data-testid", "map-upload-info-text")
                content = "Maps enter a brief review queue before becoming available for matchmaking. " +
                          "Reserved and tutorial maps are excluded from normal matchmaking."
            }
        }

        // Footer — buttons match the general UI button style
        // (min-width: 160px, height: 48px, padding: 14px 24px) instead of
        // stretching to the full panel width. Less wide, taller, centered.
        hPanel(
            className = "map-upload-modal-footer",
            justify = JustifyContent.CENTER,
            alignItems = AlignItems.CENTER,
            spacing = 14
        ) {
            width = 100.perc
            button("Cancel", className = "btn-secondary-action map-upload-modal-btn") {
                onClick { dismiss() }
                setAttribute("data-testid", "map-upload-cancel")
            }
            button("Publish", className = "btn-primary-action map-upload-modal-btn") {
                onClick { onPublishClicked() }
                disabled = true
                setAttribute("data-testid", "map-upload-publish")
            }
        }

        // Attach native HTML5 drag-and-drop to the window so file drops
        // are captured even when the modal is in any state. Window-level
        // listeners are reliable because the dropZone div's display:none
        // (during show/hide) would otherwise miss the drop event.
        attachNativeDragDrop()
    }

    private fun attachNativeDragDrop()
    {
        kotlinx.browser.window.addEventListener("dragover", { event ->
            event.preventDefault()
            dropZone.addCssClass("drop-zone-dragging")
            null
        })
        kotlinx.browser.window.addEventListener("dragleave", { event ->
            // Always remove the dragging class on dragleave — easier than
            // doing contains() on a dynamic EventTarget.
            dropZone.removeCssClass("drop-zone-dragging")
            null
        })
        kotlinx.browser.window.addEventListener("drop", { event ->
            event.preventDefault()
            dropZone.removeCssClass("drop-zone-dragging")
            val dynamic = event.asDynamic()
            val dataTransfer = dynamic.dataTransfer
            if (dataTransfer != null) {
                val files = dataTransfer.files
                if (files != null && files.length > 0) {
                    val firstFile = files[0] as? File
                    if (firstFile != null) {
                        Logger.info(LogCategory.UI, "MapUploadModal: drag-drop event file.name=${firstFile.name} file.size=${firstFile.size}")
                        handleFileSelected(firstFile)
                    }
                }
            }
            null
        })
        Logger.info(LogCategory.UI, "MapUploadModal: drag-and-drop listeners attached")
    }

    private fun handleFileSelected(file: File)
    {
        selectedFile = file
        Logger.info(LogCategory.UI, "MapUploadModal: file selected name=${file.name} size=${file.size}")
        showValidatedState(file)
    }

    private fun showValidatedState(file: File)
    {
        val prior = currentState
        currentState = State.VALIDATED
        Logger.info(LogCategory.UI, "MapUploadModal: state ${prior} -> VALIDATED file=${file.name} size=${file.size}")
        dropZone.setAttribute("data-state", "validated")
        dropZone.removeCssClass("drop-zone-idle")
        dropZone.addCssClass("drop-zone-validated")
        // Pre-fill the name input with the file name minus extension. The player
        // can rename before publishing; the server-side dedupe preflight keys on
        // this string (case-insensitive trimmed match), so whitespace + case in
        // the input matters.
        val nameWithoutExt = file.name.replace(Regex("\\.[^.]+$"), "")
        nameInput.value = nameWithoutExt
        metaPreview.display = Display.FLEX
        errorBanner.display = Display.NONE
        // The full metadata card is built in Task 19 once the validation
        // RPC is wired. For now, show filename + size as a placeholder.
        metaPreview.removeAll()
        metaPreview.add(div {
            p("TODO_AFTER_UPLOAD_RPC: meta-card from real validation") {
                color = Color("#a8b1d1")
            }
            p("filename: ${file.name}  size: ${file.size}b") {
                color = Color.name(Col.LIGHTGRAY)
            }
        })
        // Re-enable the publish button
        val publishBtn = querySelectorPublishButton()
        if (publishBtn != null) {
            publishBtn.disabled = false
            publishBtn.textContent = "Publish to review"
            publishBtn.setAttribute("data-state", "validated")
        }
    }

    private fun onPublishClicked()
    {
        Logger.info(LogCategory.UI, "MapUploadModal: publish-button click prior_state=${currentState}")
        val prior = currentState
        currentState = State.PUBLISHING
        Logger.info(LogCategory.UI, "MapUploadModal: state ${prior} -> PUBLISHING file=${selectedFile?.name}")
        val file = selectedFile
        if (file == null)
        {
            Logger.warn(LogCategory.UI, "MapUploadModal: onPublishClicked called with null selectedFile; ignoring")
            currentState = State.IDLE
            return
        }
        val invoker = ServerExtendBridge.rpcInvoker
        if (invoker == null)
        {
            Logger.warn(LogCategory.UI, "MapUploadModal: ServerExtendBridge.rpcInvoker is null (disconnected); aborting publish")
            currentState = State.VALIDATED
            return
        }
        // Disable publish + cancel + close buttons while the RPC is in-flight.
        val publishBtn = querySelectorPublishButton()
        publishBtn?.disabled = true
        publishBtn?.textContent = "Publishing…"
        publishBtn?.setAttribute("data-state", "publishing")
        val cancelBtn = querySelectorCancelButton()
        cancelBtn?.disabled = true
        val closeBtn = getElement()?.querySelector("[data-testid='map-upload-close']") as? org.w3c.dom.HTMLButtonElement
        closeBtn?.disabled = true

        // Capture the (possibly edited) name input value as the catalogue name.
        // Trim to mirror the server-side `mapName.trim()` comparison.
        val mapName = nameInput.value?.trim().orEmpty()

        // Throbber MessageBox during the long Bedrock safety pass.
        // No cancel button. Auto-replaced on success/error by the
        // notification handlers in Task 5 (via the successCallback / errorCallback
        // hooks that CollectionOverlay wires up at construction time).
        val throbber = ui.MessageBox(
            boxTitle = "Uploading map…",
            message = "Validating and saving. This may take up to 30 seconds.",
            showThrobber = true
        )
        KEnv.mainRoot?.add(throbber)

        // Read the file as bytes via FileReader -> Uint8Array -> ByteArray.
        val reader = FileReader()
        reader.onload = { _ ->
            try
            {
                val arrayBuffer = reader.result as org.khronos.webgl.ArrayBuffer
                val u8 = Uint8Array(arrayBuffer)
                val bytes = ByteArray(u8.length)
                for (i in bytes.indices) bytes[i] = u8.asDynamic()[i] as Byte
                MainScope().launch {
                    val result = runCatching {
                        val response = invoker.invoke(
                            "server.extend.uploadMapGate",
                            MapUploadRequest(
                                mapPackBytes = bytes,
                                mapName = mapName
                            ),
                            MapUploadRequest.serializer()
                        )
                        if (response.error != null)
                        {
                            val errMsg = response.error?.message ?: response.error?.code?.toString() ?: "Unknown RPC error"
                            throw RuntimeException("uploadMapGate RPC error: $errMsg")
                        }
                        val payload = response.result
                            ?: throw RuntimeException("uploadMapGate returned null result")
                        RpcJson.decodeFromJsonElement(MapUploadGateResponse.serializer(), payload)
                    }
                    throbber.hide()
                    result.fold(
                        onSuccess = { response ->
                            if (response.accepted)
                            {
                                Logger.info(
                                    LogCategory.UI,
                                    "MapUploadModal: uploadMapGate accepted mapId=${response.mapId} mapName=${response.mapName}"
                                )
                                // The Map.Upload.Success notification handler (Task 5) dismisses
                                // the modal + shows the success MessageBox + re-fetches the catalogue.
                                // We do NOT dismiss here to avoid a double-dismiss race.
                            }
                            else
                            {
                                val reason = response.reason ?: "Unknown rejection"
                                Logger.warn(LogCategory.UI, "MapUploadModal: uploadMapGate rejected reason='$reason'")
                                dismiss()
                                showUploadFailedMessageBox(reason)
                            }
                        },
                        onFailure = { err ->
                            Logger.error(LogCategory.UI, "MapUploadModal: uploadMapGate RPC threw: ${err.message}")
                            dismiss()
                            showUploadFailedMessageBox("Network error: ${err.message ?: err::class.js.name}")
                        }
                    )
                }
            }
            catch (e: Throwable)
            {
                Logger.error(LogCategory.UI, "MapUploadModal: FileReader onload failed: ${e.message}")
                throbber.hide()
                dismiss()
                showUploadFailedMessageBox("File read failed: ${e.message ?: e::class.js.name}")
            }
        }
        reader.onerror = { _ ->
            Logger.error(LogCategory.UI, "MapUploadModal: FileReader.onerror: ${reader.error?.message}")
            throbber.hide()
            dismiss()
            showUploadFailedMessageBox("File read failed: ${reader.error?.message ?: "unknown"}")
        }
        reader.readAsArrayBuffer(file)
    }

    /**
     * Show the upload-failed MessageBox. Used by both the RPC-failure path
     * and the gate-rejected path. The player has to re-open the modal and
     * re-pick the file (no retry affordance).
     *
     * The MessageBox carries an OK button via `showOk = true` so the user
     * has a working dismiss affordance. Without this the box renders with
     * no buttons and the only escape is a hard browser back — visible in
     * the screenshot the operator attached.
     */
    private fun showUploadFailedMessageBox(reason: String)
    {
        val msg = ui.MessageBox(
            boxTitle = "Upload failed",
            message = "Upload failed: $reason",
            showOk = true
        )
        KEnv.mainRoot?.add(msg)
    }

    companion object
    {
        /**
         * Global hook the [MapUploadSuccessClientHandlers] calls when a
         * Map.Upload.Success notification arrives. The owning
         * CollectionOverlay registers this hook at construction time so the
         * modal knows about its catalogue-refresh responsibility without
         * taking a hard dependency on CollectionOverlay's internals.
         *
         * Nullable when no overlay is mounted.
         */
        var successCallback: ((mapName: String) -> Unit)? = null

        /**
         * Global hook the [MapUploadErrorClientHandlers] calls when a
         * Map.Upload.Error notification arrives. Symmetric with
         * [successCallback]. Nullable when no overlay is mounted.
         */
        var errorCallback: ((reason: String) -> Unit)? = null

        /**
         * Dispatched by the server-extend success notification handler.
         * Routes through the registered [successCallback] (set by
         * CollectionOverlay at construction time) so the modal can dismiss
         * + show MessageBox + trigger catalogue re-fetch.
         */
        fun handleUploadSuccessNotification(mapName: String)
        {
            Logger.info(LogCategory.UI, "MapUploadModal.handleUploadSuccessNotification mapName='$mapName'")
            successCallback?.invoke(mapName)
        }

        /**
         * Dispatched by the server-extend error notification handler.
         * Routes through the registered [errorCallback] (set by
         * CollectionOverlay at construction time).
         */
        fun handleUploadErrorNotification(reason: String)
        {
            Logger.info(LogCategory.UI, "MapUploadModal.handleUploadErrorNotification reason='$reason'")
            errorCallback?.invoke(reason)
        }

        var deleteSuccessCallback: ((mapId: String, mapName: String) -> Unit)? = null

        var deleteErrorCallback: ((reason: String) -> Unit)? = null

        fun handleDeleteSuccessNotification(mapId: String, mapName: String)
        {
            Logger.info(LogCategory.UI, "MapUploadModal.handleDeleteSuccessNotification mapId='$mapId' mapName='$mapName'")
            deleteSuccessCallback?.invoke(mapId, mapName)
        }

        fun handleDeleteErrorNotification(reason: String)
        {
            Logger.info(LogCategory.UI, "MapUploadModal.handleDeleteErrorNotification reason='$reason'")
            deleteErrorCallback?.invoke(reason)
        }
    }
    private fun querySelectorPublishButton(): org.w3c.dom.HTMLButtonElement?
    {
        return document.querySelector("[data-testid='map-upload-publish']") as? org.w3c.dom.HTMLButtonElement
    }

    private fun querySelectorCancelButton(): org.w3c.dom.HTMLButtonElement?
    {
        return document.querySelector("[data-testid='map-upload-cancel']") as? org.w3c.dom.HTMLButtonElement
    }

    /**
     * Show the modal. Toggle raw CSS visibility — do NOT call
     * KVision's display = Display.FLEX setter (which for this widget
     * re-creates children and leaves the root detached).
     */
    /**
     * Show the modal. Use a combination of inline style.display AND
     * data-hidden attribute so the modal is visible after re-open.
     *
     * KVision's display setter triggers a re-render that destroys the
     * children, so we set the inline style via raw DOM. We also clear
     * the data-hidden attribute (used by dismiss() to hide via CSS).
     */
    override fun show()
    {
        val prior = currentState
        Logger.info(LogCategory.UI, "MapUploadModal: show() prior_state=${prior}")
        currentState = State.IDLE
        selectedFile = null
        // Force display:flex via inline style. This is the KVision INIT
        // value that gets overridden by .map-upload-modal[data-hidden="true"]
        // when we want it hidden. We do NOT touch KVision's display setter
        // to avoid the parent.add() -> re-render -> child-loss cycle.
        try {
            getElement()?.let {
                it.removeAttribute("data-hidden")
                it.style.display = "flex"
            }
        } catch (e: Throwable) {
            Logger.warn(LogCategory.UI, "MapUploadModal: show() failed (non-fatal): " + e.message)
        }
        // Reset state (use direct DOM — KVision's setters may re-render)
        try {
            val dz = getElement()?.querySelector("[data-testid=\"map-upload-drop-zone\"]") as? org.w3c.dom.HTMLElement
            dz?.setAttribute("data-state", "idle")
            dz?.classList?.remove("drop-zone-validated")
            dz?.classList?.add("drop-zone-idle")
            val mp = getElement()?.querySelector("[data-testid=\"map-upload-meta-preview\"]") as? org.w3c.dom.HTMLElement
            mp?.style?.display = "none"
            val eb = getElement()?.querySelector("[data-testid=\"map-upload-error-banner\"]") as? org.w3c.dom.HTMLElement
            eb?.style?.display = "none"
            val pb = getElement()?.querySelector("[data-testid=\"map-upload-publish\"]") as? org.w3c.dom.HTMLElement
            pb?.asDynamic()?.disabled = true
            pb?.textContent = "Publish"
            pb?.removeAttribute("data-state")
            val cb = getElement()?.querySelector("[data-testid=\"map-upload-cancel\"]") as? org.w3c.dom.HTMLElement
            cb?.asDynamic()?.disabled = false
        } catch (e: Throwable) {
            Logger.warn(LogCategory.UI, "MapUploadModal: show() state reset failed (non-fatal): " + e.message)
        }
    }

    override fun hide()
    {
        Logger.info(LogCategory.UI, "MapUploadModal: hide() prior_state=${currentState}")
        super.hide()
        display = Display.NONE
        visible = false
    }

    /**
     * Dismiss the modal on cancel. Toggle raw CSS visibility on the root
     * element — do NOT use KVision's display = Display.NONE setter which
     * (for this widget) re-parents the children to .login-widget-window
     * and detaches the root, making show() unable to re-display cleanly.
     */
    fun dismiss()
    {
        Logger.info(LogCategory.UI, "MapUploadModal: dismiss() prior_state=${currentState}")
        if (this::fileInput.isInitialized) {
            try {
                fileInput.parentNode?.removeChild(fileInput)
            } catch (e: Throwable) {
                Logger.warn(LogCategory.UI, "MapUploadModal: fileInput detach failed (non-fatal): " + e.message)
            }
        }
        // Use a data-attribute + CSS attribute selector to hide. Bypasses
        // both KVision's display setter (which re-parents children) and
        // direct style.display (which KVision's resize observer overrides).
        // The CSS rule in night-mode.css is:
        //   .map-upload-modal[data-hidden="true"] { display: none !important; }
        try {
            getElement()?.setAttribute("data-hidden", "true")
        } catch (e: Throwable) {
            Logger.warn(LogCategory.UI, "MapUploadModal: data-hidden set failed (non-fatal): " + e.message)
        }
        // Do NOT call visible = false — that's a KVision setter that
        // triggers a re-render which removes the element from the parent.
        // The CSS data-hidden attribute alone is enough.
    }
}
